// Tenantra Inc. - Inline Card Form using Stripe Elements
import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreditCard, X, Loader2, CheckCircle } from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';

// Load Stripe outside component to avoid recreation on re-renders
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Stripe Element styling to match our theme
const ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '14px',
      color: '#1e293b',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': {
        color: '#94a3b8',
      },
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626',
    },
  },
};

// The actual card form component (wrapped by Elements provider)
function CardFormInner({ onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  
  const [loading, setLoading] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [billingAddress, setBillingAddress] = useState({
    line1: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'CA'
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!cardholderName.trim()) newErrors.name = 'Name is required';
    if (!billingAddress.line1.trim()) newErrors.address = 'Address is required';
    if (!billingAddress.city.trim()) newErrors.city = 'City is required';
    if (!billingAddress.postal_code.trim()) newErrors.postal = 'Postal code is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      toast.error('Stripe is not loaded yet');
      return;
    }

    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    try {
      // Step 1: Get SetupIntent client secret from backend
      const { data } = await api.post('/billing/create-setup-intent');
      const clientSecret = data.client_secret;

      // Step 2: Confirm card setup with Stripe
      const cardElement = elements.getElement(CardNumberElement);
      
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: cardholderName,
            address: {
              line1: billingAddress.line1,
              city: billingAddress.city,
              state: billingAddress.state,
              postal_code: billingAddress.postal_code,
              country: billingAddress.country
            }
          }
        }
      });

      if (error) {
        setErrors({ card: error.message });
        toast.error(error.message);
      } else if (setupIntent.status === 'succeeded') {
        // Step 3: Tell backend to set this as default payment method
        await api.post('/billing/set-default-payment-method', {
          payment_method_id: setupIntent.payment_method
        });
        
        setSuccess(true);
        toast.success('Card added successfully!');
        
        // Callback after short delay to show success state
        setTimeout(() => {
          onSuccess?.();
        }, 1500);
      }
    } catch (err) {
      console.error('Card setup error:', err);
      const message = err.response?.data?.detail || 'Failed to add card';
      setErrors({ general: message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Card Added Successfully</h3>
          <p className="text-sm text-slate-500">Your payment method has been updated.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-teal-600" />
          <h3 className="text-base font-semibold text-slate-800">Add Payment Card</h3>
        </div>
        <button 
          onClick={onCancel}
          className="text-slate-400 hover:text-slate-600 p-1"
          disabled={loading}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        {/* Cardholder Name */}
        <div>
          <Label htmlFor="cardholderName" className="text-xs text-slate-600 mb-1.5 block">
            Name on Card *
          </Label>
          <Input
            id="cardholderName"
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
            placeholder="John Smith"
            className={`h-9 text-sm ${errors.name ? 'border-red-500' : ''}`}
            disabled={loading}
          />
          {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
        </div>

        {/* Card Number */}
        <div>
          <Label className="text-xs text-slate-600 mb-1.5 block">Card Number *</Label>
          <div className={`border rounded-md px-3 py-2.5 bg-white ${errors.card ? 'border-red-500' : 'border-slate-200'}`}>
            <CardNumberElement options={ELEMENT_OPTIONS} />
          </div>
          {errors.card && <p className="text-xs text-red-500 mt-1">{errors.card}</p>}
        </div>

        {/* Expiry and CVC */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-600 mb-1.5 block">Expiry Date *</Label>
            <div className="border border-slate-200 rounded-md px-3 py-2.5 bg-white">
              <CardExpiryElement options={ELEMENT_OPTIONS} />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1.5 block">CVV *</Label>
            <div className="border border-slate-200 rounded-md px-3 py-2.5 bg-white">
              <CardCvcElement options={ELEMENT_OPTIONS} />
            </div>
          </div>
        </div>

        {/* Billing Address */}
        <div className="pt-2 border-t border-slate-100">
          <p className="text-xs font-medium text-slate-700 mb-3">Billing Address</p>
          
          <div className="space-y-3">
            <div>
              <Label htmlFor="address" className="text-xs text-slate-600 mb-1.5 block">
                Street Address *
              </Label>
              <Input
                id="address"
                value={billingAddress.line1}
                onChange={(e) => setBillingAddress({ ...billingAddress, line1: e.target.value })}
                placeholder="123 Main Street"
                className={`h-9 text-sm ${errors.address ? 'border-red-500' : ''}`}
                disabled={loading}
              />
              {errors.address && <p className="text-xs text-red-500 mt-1">{errors.address}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="city" className="text-xs text-slate-600 mb-1.5 block">City *</Label>
                <Input
                  id="city"
                  value={billingAddress.city}
                  onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
                  placeholder="Toronto"
                  className={`h-9 text-sm ${errors.city ? 'border-red-500' : ''}`}
                  disabled={loading}
                />
                {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city}</p>}
              </div>
              <div>
                <Label htmlFor="state" className="text-xs text-slate-600 mb-1.5 block">Province</Label>
                <Input
                  id="state"
                  value={billingAddress.state}
                  onChange={(e) => setBillingAddress({ ...billingAddress, state: e.target.value })}
                  placeholder="Ontario"
                  className="h-9 text-sm"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="postal" className="text-xs text-slate-600 mb-1.5 block">
                  Postal Code *
                </Label>
                <Input
                  id="postal"
                  value={billingAddress.postal_code}
                  onChange={(e) => setBillingAddress({ ...billingAddress, postal_code: e.target.value })}
                  placeholder="M5V 1J2"
                  className={`h-9 text-sm ${errors.postal ? 'border-red-500' : ''}`}
                  disabled={loading}
                />
                {errors.postal && <p className="text-xs text-red-500 mt-1">{errors.postal}</p>}
              </div>
              <div>
                <Label htmlFor="country" className="text-xs text-slate-600 mb-1.5 block">Country</Label>
                <select
                  id="country"
                  value={billingAddress.country}
                  onChange={(e) => setBillingAddress({ ...billingAddress, country: e.target.value })}
                  className="w-full h-9 text-sm border border-slate-200 rounded-md px-3 bg-white"
                  disabled={loading}
                >
                  <option value="CA">Canada</option>
                  <option value="US">United States</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Error message */}
        {errors.general && (
          <div className="bg-red-50 text-red-700 text-sm p-3 rounded-md">
            {errors.general}
          </div>
        )}

        {/* Submit Button */}
        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 h-9 text-sm"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!stripe || loading}
            className="flex-1 h-9 text-sm bg-teal-600 hover:bg-teal-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              'Add Card'
            )}
          </Button>
        </div>

        {/* Security Note */}
        <p className="text-xs text-slate-400 text-center pt-2">
          🔒 Your card details are securely processed by Stripe
        </p>
      </form>
    </div>
  );
}

// Main exported component with Elements wrapper
export default function AddCardForm({ onSuccess, onCancel }) {
  return (
    <Elements stripe={stripePromise}>
      <CardFormInner onSuccess={onSuccess} onCancel={onCancel} />
    </Elements>
  );
}
